import {
  Alert,
  Button,
  Column,
  Combobox,
  Dialog,
  FormField,
  Row,
  useToast,
} from "@hightouch/htui";
import { useState } from "react";

import {
  useGenerateAgenticUfmMutation,
  useMinimalDecisionEngineFlowsQuery,
} from "src/graphql";
import { useDecisionEngineContext } from "src/pages/decision-engines/decision-engine-context";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export function GenerateAgenticUfmDialog({ isOpen, onClose }: Props) {
  const { engine } = useDecisionEngineContext();
  const { toast } = useToast();
  const [flowId, setFlowId] = useState<string | undefined>();

  const flowsQuery = useMinimalDecisionEngineFlowsQuery(
    { id: engine.id },
    { select: (data) => data.decision_engine_flows },
  );
  const flows = flowsQuery.data ?? [];
  const selectedFlow = flows.find((flow) => flow.id === flowId);

  const generateMutation = useGenerateAgenticUfmMutation();

  const handleGenerate = async () => {
    if (!selectedFlow) return;
    try {
      await generateMutation.mutateAsync({ agentId: selectedFlow.id });
      toast({
        id: "generate-feature-matrix",
        title: "Generating feature matrix",
        message: "It will appear in the list when ready",
        variant: "success",
      });
      onClose();
    } catch {
      toast({
        id: "generate-feature-matrix",
        title: "Failed to start feature matrix generation",
        variant: "error",
      });
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Generate feature matrix"
      variant="form"
      width="md"
      actions={
        <Row gap={2}>
          <Button autoFocus onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            isDisabled={!selectedFlow}
            isLoading={generateMutation.isLoading}
            onClick={handleGenerate}
          >
            Generate
          </Button>
        </Row>
      }
    >
      <Column gap={4}>
        <FormField
          label="Agent"
          description="Features are drafted from this agent's parent model schema and tailored to its goal. Generation runs in the background and can take a few minutes."
        >
          <Combobox
            isLoading={flowsQuery.isLoading}
            placeholder="Select an agent..."
            value={flowId}
            onChange={(value) => setFlowId(value ?? undefined)}
            optionValue={(option) => option.id}
            optionLabel={(option) => option.name}
            options={flows}
          />
        </FormField>
        <Alert
          variant="inline"
          type="info"
          title="Generated features need review"
          message="Confirm the features are relevant to the agent's goal and that none of them use data your compliance policies restrict."
        />
      </Column>
    </Dialog>
  );
}
